import { useState } from "preact/hooks";
import type { Concert } from "../types";
import { extraClassLabel, formatDateParts, formatProvenance } from "../utils/format";

const STATUS_LABEL = { cancelled: "Cancelled", postponed: "Postponed", rescheduled: "Rescheduled" } as const;
import { focusConcertOnMap } from "../state/store";
import { hasMapLocation } from "../utils/geo";
import { ProgramList } from "./ProgramList";

/** Whether newly rendered cards start with performers revealed. Performer/role data is
 *  raw extraction output — unlike programs it passes no alias tables, agent audits, or
 *  junk filters — so it hides behind an explicit "experimental" reveal. Module-level so
 *  one reveal carries to cards rendered afterwards this session: a reader who wants
 *  performers shouldn't have to click every card. Deliberately not persisted. */
let performersDefaultOpen = false;

interface ConcertCardProps {
  concert: Concert;
}

export function ConcertCard({ concert }: ConcertCardProps) {
  const [showPerformers, setShowPerformers] = useState(performersDefaultOpen);
  const date = formatDateParts(concert.date_start);
  const title = concert.concert_title || concert.org_name;
  const showOrgLine = Boolean(concert.concert_title);
  const provenance = formatProvenance(concert.checked_at, concert.program.length > 0);
  // Venue-organizations (Wigmore Hall) present concerts in themselves — repeating the
  // name as both presenter line and venue line reads as a glitch, so the venue keeps
  // only its city half when a shown line above already carries the same name.
  const venueShown =
    concert.venue_name &&
    concert.venue_name !== title &&
    !(showOrgLine && concert.org_names?.includes(concert.venue_name));

  function handleShowOnMap() {
    focusConcertOnMap(concert);
    requestAnimationFrame(() => {
      document.querySelector(".map-pane")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  const hasProgram = concert.program.length > 0;
  // ISSUES #069: a performance the presenter's page says will not (or did not) take place stays
  // on the list — someone may hold a ticket — but reads as called off at a glance.
  const status = concert.status;
  const notPerformed = status === "cancelled" || status === "postponed";
  const newDate = status === "postponed" ? formatDateParts(concert.status_new_date ?? null) : null;
  // A bare marker ("CANCELLED", "abgesagt", "中止") only repeats the badge; quote the page when its
  // words say more — a reason, a new date ("Postponed to 12 March", "cancelled due to illness").
  const quote = concert.status_text && concert.status_text.trim().length > 16 ? concert.status_text.trim() : null;

  /* Program-first (2026-09-06): the card is organised around what is played, not around
     the presenter's page title — those titles come straight from the org's CMS ("Concert
     3", "Saturday Series", a sponsor line) and made the list read as junk. When we have a
     program it leads, and the billed title moves beneath it as provenance ("Billed as").
     A programless listing keeps the title where it was: it is all we know. */
  const titleBlock = (
    <div class={hasProgram ? "concert-billing" : undefined}>
      {hasProgram && showOrgLine && <span class="concert-billing-label">Billed as</span>}
      {(showOrgLine || !hasProgram) && <h3 class="concert-title">{title}</h3>}
      {(showOrgLine || hasProgram) && <div class="concert-org">{concert.org_name}</div>}
    </div>
  );

  return (
    <article
      class={`concert-card${hasProgram ? " has-program" : ""}${notPerformed ? " is-not-performed" : ""}`}
      role="listitem"
    >
      <div class="concert-card-main">
        <div class="concert-body">
          <div class="concert-date" aria-label="Date">
            <span class="concert-date-value">
              {date ? (
                <>
                  {/* Year always shown: the list spans centuries, so a yearless date is
                      ambiguous the moment an 1890s row can sit beside a 2026 one. */}
                  {date.weekday}, {date.month} {date.day} {date.year}
                  {date.time && (
                    <span class="concert-date-time"> · {date.time}</span>
                  )}
                </>
              ) : (
                "Date TBA"
              )}
            </span>
            {/* Text, never colour alone. A cancelled opt-in event carries both labels. */}
            {concert.extra_class && (
              <span class="concert-kind concert-kind-extra" title="Shown because “non-classical and kids’ events” is on">
                {extraClassLabel(concert.extra_class)}
              </span>
            )}
            {status ? (
              <span class={`concert-status concert-status-${status}`}>{STATUS_LABEL[status]}</span>
            ) : (
              !concert.extra_class && (
                <span class="concert-kind" title="From the presenter's own listing">Live listing</span>
              )
            )}
          </div>
          {status && (quote || newDate || concert.status_url) && (
            <p class="concert-status-note">
              {quote && <q>{quote}</q>}
              {newDate && (
                <span class="concert-status-newdate">
                  {" "}New date: {newDate.weekday}, {newDate.month} {newDate.day} {newDate.year}.
                </span>
              )}
              {concert.status_url && (
                <>
                  {" "}
                  <a href={concert.status_url} target="_blank" rel="noopener noreferrer">
                    Notice &#x2197;
                  </a>
                </>
              )}
            </p>
          )}
          {!hasProgram && titleBlock}
          <div class="concert-venue">
            <svg
              class="concert-venue-icon"
              viewBox="0 0 24 24"
              width="12"
              height="12"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"
              />
            </svg>
            {venueShown && <span>{concert.venue_name} · </span>}
            <span class="concert-city">{concert.city_name}</span>
          </div>

          <ProgramList program={concert.program} />
          {hasProgram && titleBlock}

          {concert.performers &&
            concert.performers.length > 0 &&
            (showPerformers ? (
              <div class="concert-performers">
                {concert.performers.map((p, i) => (
                  <span key={i} class="performer">
                    {p.name}
                    {p.role && <span class="performer-role">, {p.role}</span>}
                    {i < concert.performers!.length - 1 && " · "}
                  </span>
                ))}
              </div>
            ) : (
              <button
                class="performers-reveal"
                type="button"
                onClick={() => {
                  performersDefaultOpen = true;
                  setShowPerformers(true);
                }}
              >
                View performers (experimental)
              </button>
            ))}

          {/* Freshness disclosure. This data is scraped from the presenter's page at a
              point in time, so a program change after that read is invisible to us —
              stating the read date is the only honest way to present it. Stated plainly,
              never as a warning: we make no promises about re-crawl cadence, so age
              isn't an anomaly to alarm about, just a fact to disclose. */}
          {provenance && (
            <div class="concert-provenance" title={provenance.detail}>
              <time datetime={provenance.iso}>{provenance.label}</time>
              <span class="sr-only">. {provenance.detail}</span>
            </div>
          )}
        </div>
      </div>

      <div class="concert-actions">
        {hasMapLocation(concert) && (
          <button class="concert-map-link" onClick={handleShowOnMap} type="button">
            Show on map
          </button>
        )}
        <a
          class="concert-source"
          href={concert.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Tickets &amp; info &#x2197;
        </a>
      </div>
    </article>
  );
}
